import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import { faHeart, faUndo } from '@fortawesome/pro-regular-svg-icons';

export default function useFavoriteItem({
  favorite,
  timeoutActive,
  index,
}: {
  favorite: any;
  timeoutActive: boolean;
  index?: number;
}) {
  /********************
   * COMPOSITIONS      *
   ********************/
  const route = useRoute();
  const { getDecimalSeparator } = usePriceFormatter();
  const { getContextForCP } = useECommerceContext();
  const { trackProductListClick } = useTracking();

  /********************
   * REFS & VARS       *
   ********************/
  const markedForRemoval = ref(false);
  const hoverOnIcon = ref(false);

  const favoriteIcon = computed(() => {
    return hoverOnIcon.value ? faHeart : faHeartSolid;
  });

  const undoIcon = computed(() => {
    return faUndo;
  });

  const comparePrice = computed(() => {
    if (hasPrice(favorite.price && favorite.price.price_off_season)) {
      return favorite.price.price_off_season;
    }

    if (hasPrice(favorite.price && favorite.price.price1)) {
      return favorite.price.price1;
    }

    return null;
  });

  const priceNumberBeforeComma = computed(() => {
    const decimal = parseFloat(comparePrice.value).toFixed(2);
    return decimal.split('.')[0];
  });

  const priceDecimals = computed(() => {
    const decimal = parseFloat(comparePrice.value).toFixed(2);
    return decimal.split('.')[1];
  });

  const decimalSeparator = computed(() => {
    return getDecimalSeparator(comparePrice.value);
  });

  /********************
   * FUNCTIONS         *
   ********************/
  function setHoverState(isActive: boolean) {
    hoverOnIcon.value = isActive;
  }

  function markForRemoval() {
    hoverOnIcon.value = false;
    markedForRemoval.value = true;
  }

  function undoMarkForRemoval() {
    hoverOnIcon.value = false;
    markedForRemoval.value = false;
  }

  function unmarkedForRemoval(event) {
    if (!timeoutActive || event.detail.lastRemovedIndex === index) {
      markedForRemoval.value = false;
    }
  }

  function hasPrice(price) {
    return price && price !== 0;
  }

  function onFavoriteClicked(position: number) {
    trackProductListClick({
      product: {
        id: favorite.camping_id.toString(),
        name: favorite.name,
        category: 'campsite',
        currency: 'EUR',
        price: Math.round((favorite.price?.price_off_season || favorite.price?.price1 || 0) * 100) / 100,
        position: position + parseInt((route.query?.offset as string) || '0'),
      },
      name: 'favorites',
      context: getContextForCP(favorite),
    });
  }

  /********************
   * HOOKS             *
   ********************/
  onMounted(() => {
    window.addEventListener('favorites.updated', unmarkedForRemoval);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('favorites.updated', unmarkedForRemoval);
  });

  return {
    comparePrice,
    decimalSeparator,
    favoriteIcon,
    hoverOnIcon,
    markForRemoval,
    markedForRemoval,
    onFavoriteClicked,
    priceDecimals,
    priceNumberBeforeComma,
    setHoverState,
    undoIcon,
    undoMarkForRemoval,
  };
}
